/**
 * Shared, platform-agnostic contract for the health-store integration.
 *
 * This is what the tickets call "the `HealthProvider` interface" — the seam that
 * #29 (iOS HealthKit), #30 (Android Health Connect) and #36 (body metrics) all
 * build on. Platform implementations live in the sibling
 * `health-provider.{ios,android,web}.ts` files (Metro extension resolution), and
 * the React layer consumes them through `features/health/contexts/health/Health.tsx`.
 *
 * No React Native imports here so this module stays usable from the pure-logic
 * unit-test project (`*.unit.test.ts`, Node env).
 */

/** Where a reading originated. Shown as a data-source badge in the UI. */
export enum HealthDataSource {
  APPLE_HEALTH = 'APPLE_HEALTH',
  HEALTH_CONNECT = 'HEALTH_CONNECT',
}

/** Coarse permission state, normalized across HealthKit and Health Connect. */
export enum HealthPermissionStatus {
  /** No health store on this platform/device (web, unsupported OS). */
  UNAVAILABLE = 'unavailable',
  /** Never asked, or the OS won't reveal the outcome yet. */
  UNDETERMINED = 'undetermined',
  /** Asked and nothing granted. */
  DENIED = 'denied',
  /** Some but not all requested types granted (Health Connect only — HealthKit
   * hides read grants, so iOS can't distinguish partial from full). */
  PARTIAL = 'partial',
  /** Read access available for all requested scopes. */
  GRANTED = 'granted',
}

/**
 * Groups of data types requested together. Kept coarse so callers ask for
 * capabilities, not raw HealthKit/Health Connect identifiers.
 */
export enum HealthScope {
  /** Recorded training sessions: heart rate, active energy, exercise sessions. */
  WORKOUT = 'workout',
  /** Body composition: weight, body-fat %, lean mass. */
  BODY = 'body',
}

export interface HealthDateRange {
  from: Date;
  to: Date;
}

/** A recorded workout session normalized from the health store (for #29/#30). */
export interface RecordedSessionSample {
  externalId: string;
  source: HealthDataSource;
  startedAt: Date;
  endedAt: Date;
  /** Beats per minute. */
  avgHeartRate?: number;
  maxHeartRate?: number;
  /** Active energy in kilocalories. */
  activeCalories?: number;
  durationSec?: number;
}

/** A body-composition reading normalized from the health store (for #36). */
export interface BodyMeasurementSample {
  externalId: string;
  source: HealthDataSource;
  measuredAt: Date;
  weightKg?: number;
  /** 0–100. */
  bodyFatPercent?: number;
  leanMassKg?: number;
  bmi?: number;
}

/**
 * The platform abstraction. One implementation per platform; a web/other no-op
 * keeps callers branch-free. Reads are skeleton stubs in this slice — the
 * concrete queries land with #29 (workouts) and #36 (body metrics).
 */
export interface HealthProvider {
  /** Which store this implementation talks to, or `null` when unavailable. */
  readonly source: HealthDataSource | null;
  /** Synchronous capability check — cheap enough to gate rendering. */
  isAvailable(): boolean;
  getPermissionStatus(scopes: HealthScope[]): Promise<HealthPermissionStatus>;
  requestPermissions(scopes: HealthScope[]): Promise<HealthPermissionStatus>;
  /** Opens the OS health-management UI (Health Connect settings / the Health
   * app) so the user can change what they share. */
  openSettings(): Promise<void>;
  readWorkoutSessions(range: HealthDateRange): Promise<RecordedSessionSample[]>;
  readBodyMeasurements(
    range: HealthDateRange,
  ): Promise<BodyMeasurementSample[]>;
}
